// Which health icon a specialty / category shows — the client half of the
// icon feature. The database stores only an ICON KEY per row; the drawings
// ship inside this bundle (src/icons/health/, generated). This module fetches
// the id → key map ONCE per app start from GET /speciality/icons and serves it
// from a module-level cache, so a page that renders two hundred specialty
// chips never issues two hundred requests.
//
// Stale-while-revalidate: the last good map is kept in localStorage and
// returned immediately on the next start, while a fresh copy is fetched in
// the background. The server tags the map with a `version` (the newest
// updatedAt among the rows) and honours If-None-Match, so the background
// refresh is a 304 most of the time.
//
// This console is also the WRITER of those keys, which is why it exposes
// invalidateSpecialtyIconMap(): after a create/update/delete the cached map
// is wrong by definition, and the next reader must go back to the server.
//
// The map also carries the platform-wide ICON STYLE ('outline' | 'solid').
// It is ONE setting for every app, not a per-call-site choice: a selected chip
// does not get a solid icon while its neighbours stay outline. Super Admin
// flips it on the Specialty icons page (setSpecialtyIconStyle below), the
// server bumps the map's version, and the other clients pick it up on their
// next start / revalidation — a few minutes, because the map is cached.

import { useEffect, useState } from 'react';
import useAxiosAuth from './hooks/useAxiosAuth';
import baseURL from './utils/baseURL';
import { healthIcon } from './icons/health';

// Same key shape as the other web clients (contract: {version, specialties,
// categories}); the "::v1" suffix lets a future shape change ignore old data.
export const STORAGE_KEY = 'specialtyIcons::v1';

/** What every client draws when nothing was ever chosen. */
export const DEFAULT_ICON_STYLE = 'outline';

const normaliseStyle = (style) => (style === 'solid' ? 'solid' : DEFAULT_ICON_STYLE);

const EMPTY_MAP = Object.freeze({ version: null, style: DEFAULT_ICON_STYLE, specialties: {}, categories: {} });

let memoryMap = null;
let inFlight = null;

// Live subscribers (the useSpecialtyIcons hook). Kept so a style saved on the
// gallery page repaints the icons already on screen without a refetch.
const listeners = new Set();
const notify = () => {
    listeners.forEach((fn) => {
        try {
            fn(memoryMap);
        } catch {
            // A subscriber that throws must not stop the others.
        }
    });
};

const isMapShape = (m) => m && typeof m === 'object' && m.specialties && typeof m.specialties === 'object' && m.categories && typeof m.categories === 'object';

const readStorage = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return isMapShape(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeStorage = (map) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        // Quota / private mode: the in-memory copy still serves this session.
    }
};

// The request itself. useAxiosAuth is the app's axios factory (a plain
// function despite the name — masterSlice's thunks call it the same way);
// it is invoked from an anonymous callback so the rules-of-hooks lint does
// not read a named module function as a component.
const fetchFromServer = () => Promise.resolve().then(async () => {
    const axiosInstance = useAxiosAuth();
    // The map the validator comes from is captured up front: a 304 means
    // "what you sent is current", and what we sent is THIS map — not whatever
    // memoryMap holds when the response lands (see below).
    const base = memoryMap;
    // Echo the ETag exactly as received (it may be quoted); the version is the
    // fallback for a copy restored from storage that predates the etag field.
    const tag = base?.etag || base?.version;
    const headers = tag ? { 'If-None-Match': tag } : undefined;
    // 304 is a success for us: axios treats it as an error status unless told
    // otherwise, and it means "what you have is current".
    const response = await axiosInstance.get(`${baseURL}/speciality/icons`, {
        headers,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    });
    if (response.status === 304) {
        // invalidateSpecialtyIconMap() ran while this request was in flight
        // (a save landed during the background revalidation): the 304 vouches
        // for a map we have since thrown away, and answering with an empty
        // map would blank every Used-by line until the next app start. Go
        // back for a full copy — memoryMap is null now, so no validator.
        if (!base || memoryMap !== base) return fetchFromServer();
        return base;
    }
    const data = response.data || {};
    return {
        version: data.version || null,
        etag: response.headers?.etag || null,
        style: normaliseStyle(data.style),
        specialties: data.specialties || {},
        categories: data.categories || {},
    };
});

/**
 * The id → icon-key map. Resolves from memory or localStorage at once when a
 * copy exists (revalidating in the background), otherwise waits for the
 * server. Never throws once a cached copy exists; before that a network
 * failure resolves to an empty map so callers fall back to the generic icon.
 */
export const getSpecialtyIconMap = async ({ force = false } = {}) => {
    if (!force && memoryMap) return memoryMap;

    if (!force && !memoryMap) {
        const stored = readStorage();
        if (stored) {
            memoryMap = stored;
            // Background revalidation — the caller already has an answer.
            refreshSpecialtyIconMap().catch(() => {});
            return memoryMap;
        }
    }

    return refreshSpecialtyIconMap();
};

/** Always goes to the server (deduplicated while a request is in flight). */
export const refreshSpecialtyIconMap = () => {
    if (inFlight) return inFlight;
    inFlight = fetchFromServer()
        .then((map) => {
            memoryMap = map;
            writeStorage(map);
            notify();
            return map;
        })
        .catch(() => memoryMap || EMPTY_MAP)
        .finally(() => { inFlight = null; });
    return inFlight;
};

/** Forget the cached map — call after any write that changes an iconKey. */
export const invalidateSpecialtyIconMap = () => {
    memoryMap = null;
    notify();
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to clear.
    }
};

/**
 * Saves the platform-wide icon style (PUT /speciality/icons/style, gated on
 * masters.manage server-side) and folds the response into the cache in place:
 * the response carries the new style and the bumped version, so the page that
 * flipped the switch repaints at once with no refetch. The stored ETag is
 * dropped because it validated the previous version. Rejects with the axios
 * error so the caller can revert and show the server's message.
 */
export const setSpecialtyIconStyle = (style) => Promise.resolve().then(async () => {
    const axiosInstance = useAxiosAuth();
    const response = await axiosInstance.put(`${baseURL}/speciality/icons/style`, { style: normaliseStyle(style) });
    const data = response.data || {};
    const base = memoryMap || readStorage() || EMPTY_MAP;
    memoryMap = {
        ...base,
        version: data.version || base.version || null,
        etag: null,
        style: normaliseStyle(data.style),
        specialties: base.specialties || {},
        categories: base.categories || {},
    };
    writeStorage(memoryMap);
    notify();
    return memoryMap.style;
});

/**
 * The map as a component sees it: `{ specialties, categories, style }`, served
 * from cache on the first render when there is one, kept in step with later
 * refreshes / invalidations / style saves through the listener set.
 */
export const useSpecialtyIcons = () => {
    const [map, setMap] = useState(() => memoryMap || readStorage() || EMPTY_MAP);

    useEffect(() => {
        let alive = true;
        const onChange = (next) => {
            if (alive) setMap(next || EMPTY_MAP);
        };
        listeners.add(onChange);
        getSpecialtyIconMap().then(onChange).catch(() => {});
        return () => {
            alive = false;
            listeners.delete(onChange);
        };
    }, []);

    return {
        specialties: map?.specialties || {},
        categories: map?.categories || {},
        style: normaliseStyle(map?.style),
    };
};

/** Just the platform style — the common case for a render call site. */
export const useSpecialtyIconStyle = () => useSpecialtyIcons().style;

/**
 * THE render rule, identical in every client: the specialty's own key wins,
 * then its category's, then null (the caller draws the generic stethoscope).
 */
export const resolveSpecialtyIconKey = (map, specialtyId, categoryId) => {
    if (!map) return null;
    return (specialtyId && map.specialties?.[specialtyId])
        || (categoryId && map.categories?.[categoryId])
        || null;
};

/** Convenience: the component for that rule, or null. */
export const resolveSpecialtyIcon = (map, specialtyId, categoryId) =>
    healthIcon(resolveSpecialtyIconKey(map, specialtyId, categoryId), normaliseStyle(map?.style));

/**
 * Inverts the map into iconKey → [{ type, id, name }], for the gallery's
 * "Used by" line. Names come from the two master lists the caller already
 * loaded; an id with no name (inactive, deleted since) is skipped rather than
 * shown as a bare code.
 */
export const buildIconUsage = (map, specialties = [], categories = []) => {
    const usage = {};
    const push = (key, entry) => {
        if (!key) return;
        if (!usage[key]) usage[key] = [];
        usage[key].push(entry);
    };
    const spNames = new Map(specialties.map((s) => [s.specialtyId, s.name]));
    const catNames = new Map(categories.map((c) => [c.spcategoryId, c.spcategoryName]));
    Object.entries(map?.specialties || {}).forEach(([id, key]) => {
        const name = spNames.get(id);
        if (name) push(key, { type: 'specialty', id, name });
    });
    Object.entries(map?.categories || {}).forEach(([id, key]) => {
        const name = catNames.get(id);
        if (name) push(key, { type: 'category', id, name });
    });
    return usage;
};
